import React, { useState, useEffect } from 'react';
import api from '../../services/api';

function TTSQueueMonitor() {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    fetchQueue();
    
    if (autoRefresh) {
      const interval = setInterval(fetchQueue, 3000); // Refresh every 3 seconds
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  const fetchQueue = async () => {
    try {
      const response = await api.get('/tts/queue');
      setQueue(response.data.queue || []);
      setError('');
    } catch (err) {
      if (err.response?.status !== 404) {
        setError('Fout bij ophalen TTS queue');
      } else {
        setQueue([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const clearQueue = async () => {
    if (!window.confirm(`Weet je zeker dat je alle ${queue.length} berichten wilt verwijderen uit de queue?`)) {
      return;
    }
    
    try {
      await api.delete('/tts/queue');
      fetchQueue();
    } catch (err) {
      setError('Fout bij wissen van queue');
    }
  };

  if (loading) {
    return <div className="admin-section"><p>Laden...</p></div>;
  }

  return (
    <div className="admin-section">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2>📻 TTS Queue Monitor</h2>
        <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
          />
          Auto-refresh (3s)
        </label>
      </div>

      {error && <div className="error-message">{error}</div>}

      {queue.length === 0 ? (
        <div className="info-box" style={{ textAlign: 'center', padding: '40px', background: '#f0f0f0', borderRadius: '8px' }}>
          <p style={{ fontSize: '48px', margin: '0 0 10px 0' }}>✅</p>
          <h3>Queue is leeg</h3>
          <p>Er staan momenteel geen TTS berichten in de wachtrij.</p>
        </div>
      ) : (
        <div className="queue-container">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <p style={{ margin: 0, color: '#666' }}>
              <strong>{queue.length}</strong> {queue.length === 1 ? 'bericht' : 'berichten'} in wachtrij
            </p>
            <button
              onClick={clearQueue}
              style={{
                background: '#f44336',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 'bold'
              }}
            >
              🗑️ Wis hele queue
            </button>
          </div>
          
          {queue.map((item, index) => (
            <div key={index} className="queue-item" style={{
              background: index === 0 ? '#e3f2fd' : '#fff',
              border: index === 0 ? '2px solid #2196f3' : '1px solid #ddd',
              borderRadius: '8px',
              padding: '15px',
              marginBottom: '10px',
              position: 'relative'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                    <span style={{ 
                      background: index === 0 ? '#2196f3' : '#999',
                      color: 'white',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>
                      {index === 0 ? '▶ VOLGENDE' : `#${index + 1}`}
                    </span>
                    <span style={{
                      background: getVoiceColor(item.voice),
                      color: 'white',
                      padding: '4px 10px',
                      borderRadius: '4px',
                      fontSize: '11px'
                    }}>
                      {getVoiceName(item.voice)}
                    </span>
                  </div>
                  
                  <div style={{
                    background: '#f9f9f9',
                    padding: '12px',
                    borderRadius: '4px',
                    border: '1px solid #e0e0e0',
                    fontFamily: 'monospace',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}>
                    {item.text}
                  </div>
                  
                  {item.queuedAt && (
                    <div style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
                      <strong>Toegevoegd:</strong> {new Date(item.queuedAt).toLocaleString('nl-NL')}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="info-box" style={{ marginTop: '20px', background: '#fff9e6', border: '1px solid #ffe082', padding: '15px', borderRadius: '8px' }}>
        <h4 style={{ margin: '0 0 10px 0' }}>ℹ️ Over TTS Queue</h4>
        <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '14px', lineHeight: '1.8' }}>
          <li>Berichten worden in volgorde afgespeeld tussen nummers</li>
          <li>Het eerste bericht in de lijst wordt als volgende afgespeeld</li>
          <li>AI-gegenereerde track announcements hebben prioriteit over de queue</li>
          <li>Na afspelen wordt het bericht automatisch verwijderd</li>
        </ul>
      </div>
    </div>
  );
}

function getVoiceName(voice) {
  const voices = {
    'nl_BE-rdh-medium': '🇧🇪 Ruben (Man)',
    'nl_BE-nathalie-medium': '🇧🇪 Nathalie (Vrouw)',
    'nl_NL-pim-medium': '🇳🇱 Pim (Man)',
    'nl_NL-ronnie-medium': '🇳🇱 Ronnie (Man, Energiek)'
  };
  return voices[voice] || voice;
}

function getVoiceColor(voice) {
  if (voice.includes('BE')) return '#ff6b35';
  if (voice.includes('pim')) return '#4ecdc4';
  if (voice.includes('ronnie')) return '#f6b73c';
  if (voice.includes('nathalie')) return '#ff8fab';
  return '#95a5a6';
}

export default TTSQueueMonitor;
